import { Fragment, memo, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import { Collapse } from '@mui/material'
import { Checkbox, CircularProgress, NotFound, SortLabel } from 'components'
import { theme } from 'styles/theme/hav-theme'

import * as S from './styles'

export type TextAlignOptions = 'left' | 'center' | 'right'

type ColumnAlign = {
  colIndex: number
  textAlign: TextAlignOptions
}

export type TableSubRow = {
  id: string
  content: ReactNode[] | ReactNode
}

type BodyData = {
  id: string
  checked?: boolean
  disableCheckbox?: boolean
  disabled?: boolean
  expanded?: boolean
  content: ReactNode[]
  subRows?: TableSubRow[]
  subRowFlex?: boolean
  linkNavigate?: string
  colunsNotNavigate?: number[]
  onClick?: () => void
  onDoubleClick?: () => void
  hasAsyncSubRowExpand?: boolean
}

export type HeadItemType = {
  label: string | React.ReactElement
  columnName: string
  actionsLabel?: boolean
  sortable?: boolean
}

export type SortFilterType = {
  column?: string
  isCrescent?: boolean
}

export type TableProps = {
  headData: HeadItemType[]
  bodyData: BodyData[]
  toggleExpand?: (id: string) => void
  hasCheckbox?: boolean
  disableGlobalCheckbox?: boolean
  checkboxSize?: 'default' | 'small'
  setChecked?: (checked: boolean, id?: string) => void
  loadingChecked?: boolean
  summaryRow?: ReactNode[]
  notFoundTitle?: string
  notFoundDescription?: string | React.ReactElement
  notFoundComponent?: React.ReactNode
  distributeWidth?: boolean
  notScrollable?: boolean
  maxHeight?: number | string
  rowHeight?: number
  fixedHead?: boolean
  fixedFirstCol?: boolean
  fixedLeftCols?: number[]
  fixedLeftColsWidth?: number
  customFixedLeftColsWidth?: number[]
  dense?: boolean
  valign?: 'middle' | 'bottom' | 'baseline' | 'top'
  actionWidth?: number
  actionColumns?: number[]
  iconColumns?: number[]
  inputAlign?: TextAlignOptions
  customAlign?: ColumnAlign[]
  tableRef?: React.MutableRefObject<null>
  isPrinting?: boolean
  isLoading?: boolean
  hasAsyncExpand?: boolean
  iconColor?: string
  sortValue?: SortFilterType
  sortHandle?: (sortFilter: SortFilterType) => void
  onScroll?: () => void
}

const hasExpandableRows = (bodyData: BodyData[]) =>
  useMemo(
    () => bodyData.some((item) => item.subRows?.length || item.hasAsyncSubRowExpand),
    [bodyData],
  )

export const Table = ({
  headData,
  bodyData,
  hasCheckbox = false,
  disableGlobalCheckbox = false,
  checkboxSize = 'default',
  toggleExpand = () => {
    return
  },
  setChecked = () => {
    return
  },
  loadingChecked = false,
  summaryRow,
  notFoundDescription = (
    <>
      Não encontramos o que você procurou.
      <br />
      Verifique os critérios utilizados e tente novamente.
    </>
  ),
  notFoundTitle = 'Nenhum item encontrado',
  notFoundComponent,
  distributeWidth = true,
  notScrollable = false,
  maxHeight = 'auto',
  rowHeight = 67,
  fixedHead = false,
  fixedFirstCol = false,
  fixedLeftCols = [],
  fixedLeftColsWidth = 100,
  customFixedLeftColsWidth = [],
  dense = false,
  valign = undefined,
  actionWidth = 100,
  actionColumns = [],
  iconColumns = [],
  inputAlign,
  customAlign = [],
  tableRef,
  isPrinting = false,
  isLoading = false,
  hasAsyncExpand = false,
  iconColor = theme.colors.primary.main,
  sortValue,
  sortHandle,
  onScroll,
}: TableProps) => {
  const expandableRows = hasExpandableRows(bodyData) || hasAsyncExpand
  const colSpan = headData.length + (hasCheckbox ? 1 : 0) + (expandableRows ? 1 : 0)
  const [linkMouseDown, setLinkMouseDown] = useState(false)
  const [linkDragActive, setLinkDragActive] = useState(false)
  const [sortFilter, setSortFilter] = useState<SortFilterType>({})

  const [expandedId, setExpandedId] = useState('')

  const handleLinkDown = () => setLinkMouseDown(true)
  const handleLinkUp = () => setLinkMouseDown(false)
  const handleLinkMove = () => {
    if (linkMouseDown) setLinkDragActive(true)
    else setLinkDragActive(false)
  }
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (linkDragActive) e.preventDefault()
  }

  const verifyAllChecked = () => {
    return bodyData.every((item) => item.checked || item.disableCheckbox)
  }
  const verifyAllDisabled = () => {
    return bodyData.every((item) => item.disableCheckbox)
  }
  const verifySomeChecked = () => {
    if (!verifyAllChecked()) return bodyData.some((item) => item.checked)
    else return false
  }

  const handleGlobalCheckbox = () => {
    if (verifyAllChecked()) {
      setChecked(false)
    } else {
      setChecked(true)
    }
  }

  const isIconCol = (index: number) => {
    return iconColumns.includes(index)
  }

  const isFixedCol = (index: number) => {
    return fixedFirstCol && index === 0
  }

  const isFixedLeft = (index: number) => {
    return fixedLeftCols.includes(index)
  }

  const isFixedAction = (index: number) => {
    return actionColumns.includes(index)
  }

  const getFixedLeftOffset = (index: number) => {
    if (customFixedLeftColsWidth.length) {
      const colsOffset = customFixedLeftColsWidth
        .slice(0, index)
        .reduce((acc, item) => acc + item, 0)
      return colsOffset + (hasCheckbox ? 51 : 0) + (expandableRows ? 35 : 0)
    }
    return index * fixedLeftColsWidth + (hasCheckbox ? 51 : 0) + (expandableRows ? 35 : 0)
  }

  const getActionOffset = (index: number) => {
    return (headData.length - index - 1) * actionWidth
  }

  const getLeftDividerOffset = () => {
    if (customFixedLeftColsWidth.length) {
      const dividerOffset = customFixedLeftColsWidth.reduce((acc, item) => acc + item, 0)
      return dividerOffset + (hasCheckbox ? 51 : 0) + (expandableRows ? 35 : 0)
    }
    return (
      fixedLeftCols.length * fixedLeftColsWidth + (hasCheckbox ? 51 : 0) + (expandableRows ? 35 : 0)
    )
  }

  const getFixedLeftColWidth = (index: number) => {
    if (customFixedLeftColsWidth.length) {
      return customFixedLeftColsWidth[index]
    }
    return fixedLeftColsWidth
  }

  const getActionDividerOffset = () => {
    return actionColumns.length * actionWidth
  }

  const getColumnAlign = (index: number) => {
    if (customAlign.length) {
      const align = customAlign.find((item) => item.colIndex === index)
      if (align?.textAlign) return align.textAlign
    }
    return undefined
  }

  useEffect(() => {
    if (!isLoading) setExpandedId('')
  }, [isLoading])

  const hasActions = useMemo(() => !!actionColumns.length, [actionColumns])
  const hasFixedCols = useMemo(() => {
    return !!fixedLeftCols.length || hasActions
  }, [fixedLeftCols, hasActions])

  useEffect(() => {
    sortValue && setSortFilter(sortValue)
  }, [sortValue])

  const handleSort = (column: string, sort: string) => {
    if (sort === 'default') {
      setSortFilter({})
      sortHandle && sortHandle({})

      return
    }
    setSortFilter({
      column,
      isCrescent: sort === 'ascendent',
    })

    sortHandle && sortHandle({ column: column, isCrescent: sort === 'ascendent' })
  }

  const renderHeadItem = (item: HeadItemType, sortFilter: SortFilterType) => {
    if (item.columnName === '' || !item.sortable) return item.label

    return (
      <SortLabel
        controlSort={
          sortFilter.column === item.columnName
            ? sortFilter.isCrescent
              ? 'ascendent'
              : 'descendent'
            : 'default'
        }
        label={item.label}
        sortAction={(sort: string) => handleSort(item.columnName, sort)}
      />
    )
  }

  const renderHeadTable = () => {
    const heads: HeadItemType[] = headData as HeadItemType[]

    return [
      ...heads.map((headItem, index) => (
        <Fragment key={headItem.columnName || index}>
          {headItem?.actionsLabel ? (
            <S.ActionsLabel>{renderHeadItem(headItem, sortFilter)}</S.ActionsLabel>
          ) : (
            <>{renderHeadItem(headItem, sortFilter)}</>
          )}
        </Fragment>
      )),
    ]
  }

  const renderSubrow = (row: BodyData) => {
    if (!row.subRows?.length) return
    if (isLoading && expandedId === row.id && row.expanded) return

    return row.subRows.map((subrow, index) => (
      <S.SubRow key={subrow.id} visible={!!row.expanded}>
        {hasCheckbox && <td />}
        <S.ExpandActionWrapper fixed={!!fixedLeftCols.length}>
          {row.subRows?.length === index + 1 ? (
            <S.ExpandLineEnd customcolor={iconColor} rowHeight={rowHeight}>
              <div />
            </S.ExpandLineEnd>
          ) : (
            <S.ExpandLine customcolor={iconColor} rowHeight={rowHeight}>
              <div />
            </S.ExpandLine>
          )}
        </S.ExpandActionWrapper>
        {row.subRowFlex ? (
          <td colSpan={headData.length}>
            <Collapse in={row.expanded} timeout="auto" unmountOnExit>
              <div>{subrow.content}</div>
            </Collapse>
          </td>
        ) : (
          (Array.isArray(subrow.content) ? subrow.content : [subrow.content]).map(
            (item, subrowIndex) =>
              item ? (
                <S.SubRowItem
                  fixed={isFixedCol(subrowIndex)}
                  fixedLeft={isFixedLeft(subrowIndex)}
                  fixedLeftOffset={getFixedLeftOffset(subrowIndex)}
                  fixedLeftWidth={getFixedLeftColWidth(subrowIndex)}
                  isIcon={isIconCol(subrowIndex)}
                  key={`table-subrow-${subrowIndex}`}
                  rowHeight={rowHeight}
                  textAlign={getColumnAlign(subrowIndex)}
                >
                  <Collapse in={row.expanded} timeout="auto" unmountOnExit>
                    {item}
                  </Collapse>
                </S.SubRowItem>
              ) : (
                <td key={`empty-subrow-${subrowIndex}`} />
              ),
          )
        )}
      </S.SubRow>
    ))
  }

  const tableContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (onScroll) {
      const container = tableContentRef.current

      const handleScroll = () => {
        if (container) {
          const { scrollTop, scrollHeight, clientHeight } = container

          if (scrollTop + clientHeight >= scrollHeight) {
            onScroll()
          }
        }
      }

      if (container) {
        container.addEventListener('scroll', handleScroll)
      }

      return () => {
        if (container) {
          container.removeEventListener('scroll', handleScroll)
        }
      }
    }
  }, [])

  const renderTable = () => {
    return (
      <S.Wrapper
        hasFixedCols={hasFixedCols}
        isPrinting={isPrinting}
        maxHeight={maxHeight}
        notScrollable={notScrollable}
        ref={tableContentRef}
      >
        <S.WrapperTable ref={tableRef}>
          <S.Table cellSpacing={0} data-testid="table-component">
            <S.TableHead fixed={fixedHead}>
              <S.HeadRow>
                {hasCheckbox && (
                  <S.CheckboxWrapper data-testid="checkbox-header" fixed={!!fixedLeftCols.length}>
                    {loadingChecked ? (
                      <S.CircularProgressWrapper>
                        <CircularProgress color={iconColor} size="15px" />
                      </S.CircularProgressWrapper>
                    ) : (
                      <Checkbox
                        checked={verifyAllChecked() && !verifyAllDisabled()}
                        disabled={disableGlobalCheckbox || verifyAllDisabled()}
                        indeterminate={verifySomeChecked()}
                        onChange={() => handleGlobalCheckbox()}
                        size={checkboxSize}
                      />
                    )}
                  </S.CheckboxWrapper>
                )}
                {expandableRows && <S.ExpandActionWrapper fixed={!!fixedLeftCols.length} />}
                {renderHeadTable().map((item, index) => {
                  return (
                    <S.HeadItem
                      actionOffset={getActionOffset(index)}
                      actionWidth={actionWidth}
                      dense={dense}
                      fixed={isFixedCol(index)}
                      fixedAction={isFixedAction(index)}
                      fixedLeft={isFixedLeft(index)}
                      fixedLeftOffset={getFixedLeftOffset(index)}
                      fixedLeftWidth={getFixedLeftColWidth(index)}
                      isIcon={isIconCol(index)}
                      key={`table-head-item-${index}`}
                      textAlign={getColumnAlign(index)}
                    >
                      {item}
                    </S.HeadItem>
                  )
                })}
              </S.HeadRow>
            </S.TableHead>

            {bodyData?.length ? (
              bodyData.map((row, indexBodyData) => {
                if (!row) return
                return (
                  <Fragment key={indexBodyData}>
                    <S.RowGroup checked={row.checked} key={`row-${indexBodyData}`}>
                      <S.TableRow expanded={row.expanded}>
                        {hasCheckbox && (
                          <S.RowCheckboxWrapper
                            data-testid="checkbox-row"
                            fixed={!!fixedLeftCols.length}
                          >
                            {loadingChecked ? (
                              <S.CircularProgressWrapper>
                                <CircularProgress color={iconColor} size="15px" />
                              </S.CircularProgressWrapper>
                            ) : (
                              <Checkbox
                                checked={row.checked}
                                disabled={row.disableCheckbox}
                                onChange={() => setChecked(!row.checked, row.id)}
                                size={checkboxSize}
                              />
                            )}
                          </S.RowCheckboxWrapper>
                        )}
                        {expandableRows && (
                          <S.ExpandActionWrapper fixed={!!fixedLeftCols.length}>
                            {(row.subRows?.length ||
                              row.hasAsyncSubRowExpand ||
                              hasAsyncExpand) && (
                              <>
                                <S.ExpandButton
                                  disabled={isLoading}
                                  onClick={() => {
                                    toggleExpand(row.id)
                                    setExpandedId(row.id)
                                  }}
                                  type="button"
                                >
                                  {isLoading && expandedId === row.id ? (
                                    <S.IconWrapper customcolor={iconColor}>
                                      <CircularProgress color={iconColor} size="15px" />
                                    </S.IconWrapper>
                                  ) : row.expanded ? (
                                    <>
                                      <S.IconWrapper customcolor={iconColor}>
                                        <S.CollapseIcon customcolor={iconColor} />
                                      </S.IconWrapper>
                                      <S.ExpandLineStart
                                        customcolor={iconColor}
                                        expanded={row.expanded}
                                        rowHeight={rowHeight}
                                      />
                                    </>
                                  ) : (
                                    <S.IconWrapper customcolor={iconColor}>
                                      <S.ExpandIcon customcolor={iconColor} />
                                    </S.IconWrapper>
                                  )}
                                </S.ExpandButton>
                              </>
                            )}
                          </S.ExpandActionWrapper>
                        )}
                        {/** Main line content **/}
                        {row.content.map((item, indexRow) => {
                          return (
                            <S.RowItem
                              actionOffset={getActionOffset(indexRow)}
                              actionWidth={actionWidth}
                              cursorPointer={
                                !row.disabled &&
                                (Boolean(row.linkNavigate) ||
                                  Boolean(row.onClick) ||
                                  Boolean(row.onDoubleClick))
                              }
                              data-testid="row-item-table"
                              dense={dense}
                              distributeWidth={distributeWidth}
                              fixed={isFixedCol(indexRow)}
                              fixedAction={isFixedAction(indexRow)}
                              fixedLeft={isFixedLeft(indexRow)}
                              fixedLeftOffset={getFixedLeftOffset(indexRow)}
                              fixedLeftWidth={getFixedLeftColWidth(indexRow)}
                              hasCheckbox={hasCheckbox}
                              inputAlign={inputAlign}
                              isExpanded={row.expanded}
                              isIcon={isIconCol(indexRow)}
                              key={`table-row-${indexRow}`}
                              onClick={() => {
                                if (
                                  row.onClick &&
                                  !actionColumns.includes(indexRow) &&
                                  !row.disabled
                                )
                                  row.onClick()
                              }}
                              onDoubleClick={() => {
                                if (row.onDoubleClick && !row.disabled) row.onDoubleClick()
                              }}
                              rowHeight={rowHeight}
                              textAlign={getColumnAlign(indexRow)}
                              valign={valign}
                            >
                              {row.linkNavigate &&
                              row.colunsNotNavigate &&
                              !row.colunsNotNavigate.includes(indexRow) ? (
                                <S.TableLink
                                  draggable={false}
                                  onClick={handleLinkClick}
                                  onMouseDown={handleLinkDown}
                                  onMouseMove={handleLinkMove}
                                  onMouseUp={handleLinkUp}
                                  to={row.linkNavigate}
                                >
                                  {item}
                                </S.TableLink>
                              ) : (
                                item
                              )}
                            </S.RowItem>
                          )
                        })}
                      </S.TableRow>

                      {renderSubrow(row)}
                    </S.RowGroup>
                  </Fragment>
                )
              })
            ) : (
              <S.RowGroup>
                <S.TableRow>
                  <S.RowItem
                    colSpan={colSpan}
                    dense={dense}
                    distributeWidth={distributeWidth}
                    rowHeight={rowHeight}
                  >
                    {notFoundComponent || (
                      <NotFound description={notFoundDescription} title={notFoundTitle} /> // NOTE: Keeping default values, but allow to override
                    )}
                  </S.RowItem>
                </S.TableRow>
              </S.RowGroup>
            )}

            {summaryRow && (
              <S.SummaryRow>
                {hasCheckbox && <td />}
                {expandableRows && <td />}
                {summaryRow.map((item, index) => (
                  <S.RowItem
                    dense={dense}
                    distributeWidth={distributeWidth}
                    hasCheckbox={hasCheckbox}
                    key={`summary-row-${index}`}
                    rowHeight={rowHeight}
                  >
                    {item}
                  </S.RowItem>
                ))}
              </S.SummaryRow>
            )}
          </S.Table>
        </S.WrapperTable>
      </S.Wrapper>
    )
  }

  return (
    <>
      {!hasFixedCols && !hasActions ? (
        renderTable()
      ) : (
        <S.ActionTableWrapper isPrinting={isPrinting}>
          {!!fixedLeftCols.length && !!bodyData?.length && (
            <S.FixedDividerLeft offset={getLeftDividerOffset()} />
          )}
          {renderTable()}
          {!!actionColumns.length && !!bodyData?.length && (
            <S.ActionDivider offset={getActionDividerOffset()} />
          )}
        </S.ActionTableWrapper>
      )}
    </>
  )
}

export default memo(Table)
